import { useEffect, useRef, useState } from "react";

// Step 7.6 (2026-02-15) — Idle-timeout hook + companion toast component.
//
// Flow:
//   1. Timer starts when the hook mounts. Any pointer / key / scroll /
//      wheel / touch resets the "last activity" ref (no state churn).
//   2. Every 500 ms an interval checks how long since last activity. When
//      it crosses `idleMinutes * 60 - warningSeconds`, the WARNING enters
//      and the returned `warningLeft` starts counting down from
//      `warningSeconds`.
//   3. Activity during the warning window dismisses the toast and
//      restarts the full countdown from scratch.
//   4. If the countdown reaches 0, `onExpire()` fires exactly once — the
//      Shell wires this to `logout()`.
//
// Deliberately avoids using useEffect for activity handlers per render so
// listeners are attached exactly once for the lifetime of the mount.

export function useSessionIdle({ idleMinutes, warningSeconds = 60, onExpire, enabled = true }) {
  const [warningLeft, setWarningLeft] = useState(null); // null = no warning
  const lastActivityRef = useRef(Date.now());
  const inWarningRef = useRef(false);
  const expiredRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const idleMs = Math.max(1, idleMinutes) * 60 * 1000;
    const warningMs = Math.max(1, warningSeconds) * 1000;

    // Reset all internal state whenever the caller re-enables or changes
    // the timeout — otherwise a stale warning could stick around after an
    // admin bumps the timeout live from the Settings modal.
    lastActivityRef.current = Date.now();
    inWarningRef.current = false;
    expiredRef.current = false;
    setWarningLeft(null);

    const bump = () => {
      lastActivityRef.current = Date.now();
      if (inWarningRef.current) {
        inWarningRef.current = false;
        setWarningLeft(null);
      }
    };

    const events = ["mousemove", "keydown", "scroll", "touchstart", "wheel", "click"];
    events.forEach((e) => document.addEventListener(e, bump, { passive: true }));

    const tick = setInterval(() => {
      if (expiredRef.current) return;
      const idleFor = Date.now() - lastActivityRef.current;
      if (!inWarningRef.current && idleFor >= idleMs - warningMs) {
        inWarningRef.current = true;
        setWarningLeft(warningSeconds);
      } else if (inWarningRef.current) {
        const remaining = Math.ceil((idleMs - idleFor) / 1000);
        if (remaining <= 0) {
          expiredRef.current = true;
          inWarningRef.current = false;
          setWarningLeft(null);
          try { onExpire?.(); } catch { /* ignore */ }
        } else {
          setWarningLeft(remaining);
        }
      }
    }, 500);

    return () => {
      clearInterval(tick);
      events.forEach((e) => document.removeEventListener(e, bump));
    };
  }, [idleMinutes, warningSeconds, onExpire, enabled]);

  const staySignedIn = () => {
    lastActivityRef.current = Date.now();
    inWarningRef.current = false;
    setWarningLeft(null);
  };

  return { warningLeft, staySignedIn };
}
